import { AdKitKey } from "types/enums";
import type { ClubhouseAdKitDto } from "entities/responses/adkit";
import type {
  PlacementCatalogEntry,
  PlacementScreenZone,
} from "../../placementCatalog";
import { pluralize } from "../../pluralize";

export type PlacementGroupName =
  | "During a round"
  | "Before play"
  | "Range"
  | "Events and leaderboards";

/** Canonical order; a group is only shown when the campaign actually uses a placement in it. */
export const PLACEMENT_GROUP_ORDER: PlacementGroupName[] = [
  "During a round",
  "Before play",
  "Range",
  "Events and leaderboards",
];

export const GROUP_BY_KEY: Record<AdKitKey, PlacementGroupName> = {
  [AdKitKey.TEE_SIGN_3D]: "During a round",
  [AdKitKey.HOLE_LOAD_AD]: "During a round",
  [AdKitKey.SCORECARD_AD]: "During a round",
  [AdKitKey.SPONSOR_AD_3_IN_GAME]: "During a round",
  [AdKitKey.SPONSOR_AD_2_POST_SHOT]: "During a round",
  [AdKitKey.ROUND_TEE_OVERLAY]: "During a round",
  [AdKitKey.SPONSOR_AD_1_SETUP]: "Before play",
  [AdKitKey.RANGE_FAIRWAY_OVERLAY]: "Range",
  [AdKitKey.RANGE_TEE_OVERLAY]: "Range",
  [AdKitKey.SKILL_EVENT_TEE_OVERLAY]: "Events and leaderboards",
  [AdKitKey.LEADERBOARD]: "Events and leaderboards",
};

type HolePattern = "ALL_18" | "FRONT_9" | "BACK_9" | "CHOOSE_HOLES";

/**
 * The one place that looks at which holes are on/off. describeHoles() and
 * classifyHoles() both build on this rather than classifyHoles matching on
 * describeHoles()'s formatted string — a copy change to one would otherwise
 * silently break the other's classification.
 */
function classifyHolePattern(
  enabledHoles: Record<number, boolean> | undefined,
): HolePattern {
  const FRONT_9 = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
  const BACK_9 = [10, 11, 12, 13, 14, 15, 16, 17, 18] as const;
  const isOn = (hole: number): boolean => Boolean(enabledHoles?.[hole]);

  if (FRONT_9.every(isOn) && BACK_9.every(isOn)) return "ALL_18";
  if (FRONT_9.every(isOn) && BACK_9.every((h) => !isOn(h))) return "FRONT_9";
  if (BACK_9.every(isOn) && FRONT_9.every((h) => !isOn(h))) return "BACK_9";
  return "CHOOSE_HOLES";
}

/**
 * "All 18 holes" / "Front 9" / "Back 9" / "6 of 18 holes" / "" when holes
 * don't apply. Takes a plain Record rather than Models.HoleBooleanMap: that
 * type references an `Exact<>` helper that isn't defined anywhere in this
 * codebase (a pre-existing gap, not introduced here), which makes TS treat
 * it as an unresolvable "error" type wherever it's used as an annotation —
 * callers cast location.enabledHoles when passing it in.
 */
export function describeHoles(
  enabledHoles: Record<number, boolean> | undefined,
): string {
  if (!enabledHoles) return "";
  switch (classifyHolePattern(enabledHoles)) {
    case "ALL_18":
      return "All 18 holes";
    case "FRONT_9":
      return "Front 9";
    case "BACK_9":
      return "Back 9";
    case "CHOOSE_HOLES": {
      const onCount = Array.from({ length: 18 }, (_, i) => i + 1).filter(
        (hole) => enabledHoles[hole],
      ).length;
      return `${onCount} of 18 holes`;
    }
  }
}

export type HolesMode =
  | "ALL_18"
  | "FRONT_9"
  | "BACK_9"
  | "CHOOSE_HOLES"
  | "NOT_APPLICABLE";

function classifyHoles(
  isHoleEnabled: boolean,
  enabledHoles: Record<number, boolean> | undefined,
): HolesMode {
  if (!isHoleEnabled) return "NOT_APPLICABLE";
  return classifyHolePattern(enabledHoles);
}

export interface PlacementOrderedImage {
  imageUrl: string;
  /** 1-based, in the order the sim shows them. */
  position: number;
  /** 0 = shows on every selected hole; 1-18 = this image is a hole sponsor's. */
  holeNumber: number;
}

function buildOrderedImages(
  instances: { imageUrl: string; orderLocation?: number; holeNumber: number }[],
): PlacementOrderedImage[] {
  return [...instances]
    .sort((a, b) => (a.orderLocation ?? 0) - (b.orderLocation ?? 0))
    .map((instance, i) => ({
      imageUrl: instance.imageUrl,
      position: i + 1,
      holeNumber: instance.holeNumber,
    }));
}

export interface PlacementRowViewModel {
  locationId: string;
  key: AdKitKey;
  group: PlacementGroupName;
  name: string;
  whereItShows: string;
  screenZone: PlacementScreenZone;
  behaviorLabel: string;
  /** "All 18 holes · 3 images", or just "3 images" when holes don't apply. */
  summary: string;
  thumbnailUrls: string[];
  holesMode: HolesMode;
  imageSize: string;
  /** "Rotation order" for a placement that cycles on a timer, else "Order (first image shows first)". */
  orderLabel: string;
  orderedImages: PlacementOrderedImage[];
  /** Only Hole Load has this today (metadata.hasTransitions) — fade in/on screen/fade out fields show for it, nowhere else. */
  hasTransitions: boolean;
}

type CampaignForPlacementsTab = Pick<ClubhouseAdKitDto, "adLocations">;

/** One row per placement the campaign actually uses — groups and sorts come from {@link groupPlacementRows}. */
export function buildPlacementRows(
  campaign: CampaignForPlacementsTab,
  placements: PlacementCatalogEntry[],
): PlacementRowViewModel[] {
  const placementByKey = new Map(placements.map((p) => [p.key, p]));

  return (campaign.adLocations ?? []).flatMap((location) => {
    const placement = placementByKey.get(location.adKitMetaDataId);
    if (!placement) return [];

    const instances = location.adInstances ?? [];
    const imageIds = new Set(instances.map((i) => String(i.imageId)));
    // location.enabledHoles is typed Models.HoleBooleanMap, which references
    // an undefined `Exact<>` helper (pre-existing, see describeHoles) —
    // TS treats it as an "error" type, so the cast routes around that rather
    // than being necessary in the normal sense.
    const enabledHoles = location.enabledHoles as
      | Record<number, boolean>
      | undefined;
    const holesText = placement.isHoleEnabled
      ? describeHoles(enabledHoles)
      : "";
    const summary = [holesText, pluralize(imageIds.size, "image")]
      .filter(Boolean)
      .join(" · ");

    return [
      {
        locationId: String(location.id),
        key: location.adKitMetaDataId,
        group: GROUP_BY_KEY[location.adKitMetaDataId],
        name: placement.name,
        whereItShows: placement.whereItShows,
        screenZone: placement.screenZone,
        behaviorLabel: placement.howItChanges,
        summary,
        thumbnailUrls: [...new Set(instances.map((i) => i.imageUrl))].slice(
          0,
          3,
        ),
        holesMode: classifyHoles(placement.isHoleEnabled, enabledHoles),
        imageSize: placement.imageSize,
        orderLabel: placement.hasDynamicRotation
          ? "Rotation order"
          : "Order (first image shows first)",
        orderedImages: buildOrderedImages(instances),
        hasTransitions: placement.hasTransitions,
      },
    ];
  });
}

export interface PlacementGroup {
  name: PlacementGroupName;
  rows: PlacementRowViewModel[];
}

export function groupPlacementRows(
  rows: PlacementRowViewModel[],
): PlacementGroup[] {
  return PLACEMENT_GROUP_ORDER.map((name) => ({
    name,
    rows: rows.filter((row) => row.group === name),
  })).filter((group) => group.rows.length > 0);
}

/** Placement names the campaign doesn't use yet, for the "Add a placement" footer line. */
export function buildUnusedPlacementNames(
  campaign: CampaignForPlacementsTab,
  placements: PlacementCatalogEntry[],
): string[] {
  const usedKeys = new Set(
    (campaign.adLocations ?? []).map((l) => l.adKitMetaDataId),
  );
  return placements.filter((p) => !usedKeys.has(p.key)).map((p) => p.name);
}
